import { useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Compass, Loader2, Search } from 'lucide-react'
import {
  Button,
  combineQueryStates,
  EmptyView,
  FilterChip,
  Input,
  PageHeader,
  QueryErrorPanel,
  SectionHeader,
  Skeleton,
} from '@loop3/ui'
import { AskLupeButton } from '@/components/ask-lupe-button'
import { EntityGrid } from '@/components/entity-card'
import { BROWSE_LIMIT } from '@/lib/api/universe'
import { KIND_META } from '@/lib/entity-kinds'
import { useSolutionsSearch } from '@/hooks/useSolutionsSearch'
import { useDebouncedSearchParam } from '@/hooks/useDebouncedSearchParam'
import { useUniverseSearch } from '@/hooks/useUniverseSearch'
import {
  dedupeTeamPersons,
  normalizeQuery,
  rankEntities,
} from '@/lib/compute/universe'
import type { Entity, EntityKind } from '@/lib/types/universe'

const EMPTY: Entity[] = []

const DIRECTORY_KINDS: EntityKind[] = [
  'client',
  'team_member',
  'company',
  'person',
]
const GROUP_ORDER: EntityKind[] = [...DIRECTORY_KINDS, 'solution']

type KindFilter = 'all' | EntityKind
const FILTERS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  ...GROUP_ORDER.map((k) => ({ value: k, label: KIND_META[k].plural })),
]

function parseKind(raw: string | null): KindFilter {
  return FILTERS.some((f) => f.value === raw) ? (raw as KindFilter) : 'all'
}

/** Search across clients, team, companies, people and solutions, bound to `?q=` and `?kind=`. */
export function ExplorerPage() {
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const kind = parseKind(params.get('kind'))
  const text = useDebouncedSearchParam(q, (next) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        if (next) p.set('q', next)
        else p.delete('q')
        return p
      },
      { replace: true }
    )
  )

  const directoryKinds = useMemo(
    () =>
      kind === 'all'
        ? DIRECTORY_KINDS
        : DIRECTORY_KINDS.filter((k) => k === kind),
    [kind]
  )
  const wantsSolutions = kind === 'all' || kind === 'solution'
  const universe = useUniverseSearch(q, directoryKinds)
  const solutions = useSolutionsSearch(wantsSolutions ? q : '')

  // keepPreviousData keeps returning the last directory rows once the query is disabled.
  const universeData =
    directoryKinds.length > 0 ? (universe.data ?? EMPTY) : EMPTY
  const solutionsData = solutions.data ?? EMPTY
  const results = useMemo(() => {
    const ranked = rankEntities(q, [...universeData, ...solutionsData])
    return kind === 'all' ? dedupeTeamPersons(ranked) : ranked
  }, [q, kind, universeData, solutionsData])
  const groups = useMemo(
    () =>
      GROUP_ORDER.map((k) => ({
        kind: k,
        items: results.filter((e) => e.kind === k),
      })).filter((g) => g.items.length > 0),
    [results]
  )

  // Solutions-only mode never runs the directory query, so it must not gate the page.
  const { status, retry } = combineQueryStates(
    directoryKinds.length > 0 ? [universe] : []
  )
  const solutionsPending = wantsSolutions && solutions.isLoading
  const tooShortForSolutions =
    kind === 'solution' && normalizeQuery(q).length < 2
  const busy = universe.isFetching || solutions.isFetching

  const setKind = (next: KindFilter) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        if (next === 'all') p.delete('kind')
        else p.set('kind', next)
        return p
      },
      { replace: true }
    )

  return (
    <div className="mx-auto max-w-7xl pb-16">
      <PageHeader
        title="Explorer"
        subtitle="Clients, the team, companies, people and solutions Loop3 knows about."
        className="mb-0 border-b-0 pb-4"
      />

      <SearchBox
        value={text.value}
        onChange={text.set}
        busy={busy && status === 'ready'}
      />

      <div
        role="group"
        aria-label="Filter by kind"
        className="-mx-4 mt-3 flex gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0"
      >
        {FILTERS.map((f) => (
          <FilterChip
            key={f.value}
            active={kind === f.value}
            onClick={() => setKind(f.value)}
          >
            {f.label}
          </FilterChip>
        ))}
      </div>

      <div className="mt-8">
        {status === 'error' ? (
          <QueryErrorPanel onRetry={retry} />
        ) : status === 'pending' ? (
          <ResultsSkeleton />
        ) : tooShortForSolutions ? (
          <EmptyView
            icon={<Compass className="size-8" />}
            title="Search the solutions library"
            description="Type at least two characters to find things Loop3 has built."
          />
        ) : results.length === 0 && solutionsPending ? (
          <ResultsSkeleton />
        ) : results.length === 0 ? (
          <>
            <SolutionsNotice error={wantsSolutions && solutions.isError} />
            <EmptyView
              className="[overflow-wrap:anywhere]"
              icon={<Compass className="size-8" />}
              title={q ? `Nothing matches “${q}”` : 'Nothing here yet'}
              description="Lupe reads meetings, Slack and the knowledge graph, so she may know more."
              action={
                q ? (
                  <AskLupeButton question={q} label="Ask Lupe instead" />
                ) : undefined
              }
            />
          </>
        ) : (
          <>
            <ResultsMeta q={q} count={results.length} />
            <SolutionsNotice error={wantsSolutions && solutions.isError} />
            {kind === 'all' ? (
              groups.map((g) => {
                // Directory kinds are cut at the browse limit in this view; the kind filter lists them all.
                const capped =
                  g.kind !== 'solution' && g.items.length >= BROWSE_LIMIT
                return (
                  <section key={g.kind} className="mb-8">
                    <SectionHeader
                      icon={KIND_META[g.kind].icon}
                      label={KIND_META[g.kind].plural}
                      meta={capped ? `${g.items.length}+` : g.items.length}
                    />
                    <EntityGrid items={g.items} />
                    {capped && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-3"
                        onClick={() => setKind(g.kind)}
                      >
                        Show all {KIND_META[g.kind].plural.toLowerCase()} →
                      </Button>
                    )}
                  </section>
                )
              })
            ) : (
              <EntityGrid items={results} />
            )}
            {solutionsPending && <ResultsSkeleton rows={1} />}
          </>
        )}
      </div>
    </div>
  )
}

function SearchBox({
  value,
  onChange,
  busy,
}: {
  value: string
  onChange: (v: string) => void
  busy: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)

  // "/" jumps to the search box from anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (e.key !== '/' || el?.closest('input, textarea, [contenteditable]'))
        return
      e.preventDefault()
      ref.current?.querySelector('input')?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div ref={ref} className="group relative">
      <Search
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 opacity-40 transition-colors group-focus-within:text-[var(--l3-violet)] group-focus-within:opacity-100"
      />
      <Input
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the search box is the page's only purpose
        autoFocus
        type="search"
        aria-label="Search the universe"
        placeholder="Search a client, teammate, company, person or solution"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 rounded-lg bg-card pl-9 pr-10 text-sm focus-visible:border-[var(--l3-violet)] focus-visible:ring-[var(--l3-violet)]/15 md:text-sm [&::-webkit-search-cancel-button]:hidden"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
        {busy ? (
          <Loader2 className="size-4 animate-spin opacity-50" aria-hidden />
        ) : (
          <kbd className="hidden rounded border border-border px-1.5 py-0.5 font-mono text-[0.65rem] opacity-50 sm:inline">
            /
          </kbd>
        )}
      </span>
    </div>
  )
}

function ResultsMeta({ q, count }: { q: string; count: number }) {
  if (!q) return null
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <span className="eyebrow min-w-0 opacity-50 [overflow-wrap:anywhere]">
        {`${count} ${count === 1 ? 'match' : 'matches'} for “${q}”`}
      </span>
      <AskLupeButton question={q} />
    </div>
  )
}

function SolutionsNotice({ error }: { error: boolean }) {
  if (!error) return null
  return (
    <p className="mb-6 text-xs opacity-60">Solutions could not be loaded.</p>
  )
}

function ResultsSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: rows * 3 }).map((_, i) => (
        <div
          key={i}
          className="flex items-start gap-3 rounded-xl border border-border p-3.5"
        >
          <Skeleton className="size-9 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="h-2.5 w-1/2 opacity-60" />
          </div>
        </div>
      ))}
    </div>
  )
}
